// OpenCV.js helpers for the POD document scanner: lazy load, edge detection, flatten, enhance.
let cvPromise = null;

export function loadOpenCV() {
  if (cvPromise) return cvPromise;
  cvPromise = new Promise((resolve, reject) => {
    const started = Date.now();
    // The Emscripten Module is a thenable (cv.then); resolving a Promise with it loops forever, so strip it first.
    const ready = (m) => { if (typeof m.then === "function") m.then = undefined; window.cv = m; resolve(m); };
    const poll = () => {
      const cv = window.cv;
      if (cv && cv.Mat) return ready(cv);
      if (cv instanceof Promise) return cv.then(ready, reject);
      if (Date.now() - started > 90000) return reject(new Error("Scanner took too long to load"));
      setTimeout(poll, 100);
    };
    if (window.cv) return poll();
    const s = document.createElement("script");
    s.src = "/vendor/opencv.js";
    s.async = true;
    s.onload = poll;
    s.onerror = () => reject(new Error("Scanner failed to load"));
    document.body.appendChild(s);
  }).catch((e) => { cvPromise = null; throw e; });
  return cvPromise;
}

const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

function orderCorners(pts) {
  const bySum = [...pts].sort((a, b) => a.x + a.y - (b.x + b.y));
  const byDiff = [...pts].sort((a, b) => a.y - a.x - (b.y - b.x));
  return [bySum[0], byDiff[0], bySum[3], byDiff[3]]; // tl, tr, br, bl
}

function quadFromContour(cv, contour) {
  const peri = cv.arcLength(contour, true);
  for (const eps of [0.02, 0.03, 0.05, 0.08]) {
    const approx = new cv.Mat();
    cv.approxPolyDP(contour, approx, eps * peri, true);
    if (approx.rows === 4 && cv.isContourConvex(approx)) {
      const d = approx.data32S;
      approx.delete();
      return [0, 1, 2, 3].map((i) => ({ x: d[i * 2], y: d[i * 2 + 1] }));
    }
    approx.delete();
  }
  return null;
}

const polyArea = (q) => Math.abs(q.reduce((s, p, i) => { const n = q[(i + 1) % 4]; return s + p.x * n.y - n.x * p.y; }, 0)) / 2;

// Fraction of points along the quad's sides that sit on (within 2px of) a real edge.
function edgeSupport(edges, q) {
  const { cols: w, rows: h } = edges;
  const d = edges.data;
  let hit = 0;
  let tot = 0;
  for (let i = 0; i < 4; i++) {
    const a = q[i];
    const b = q[(i + 1) % 4];
    const n = Math.floor(Math.max(Math.abs(b.x - a.x), Math.abs(b.y - a.y)) / 3) + 1;
    for (let k = 0; k < n; k++) {
      const t = 0.05 + (0.9 * k) / Math.max(1, n - 1);
      const x = Math.round(a.x + (b.x - a.x) * t);
      const y = Math.round(a.y + (b.y - a.y) * t);
      tot++;
      search: for (let yy = Math.max(0, y - 2); yy <= Math.min(h - 1, y + 2); yy++) {
        for (let xx = Math.max(0, x - 2); xx <= Math.min(w - 1, x + 2); xx++) {
          if (d[yy * w + xx]) { hit++; break search; }
        }
      }
    }
  }
  return hit / Math.max(tot, 1);
}

// How much brighter the inside of the quad is than a thin ring just outside it (0..1).
function insideContrast(cv, gray, q) {
  const mask = cv.Mat.zeros(gray.rows, gray.cols, cv.CV_8U);
  const inner = new cv.Mat();
  const outer = new cv.Mat();
  const k = cv.getStructuringElement(cv.MORPH_RECT, new cv.Size(15, 15));
  const poly = cv.matFromArray(4, 1, cv.CV_32SC2, q.flatMap((p) => [Math.round(p.x), Math.round(p.y)]));
  const pv = new cv.MatVector();
  try {
    pv.push_back(poly);
    cv.fillPoly(mask, pv, new cv.Scalar(255));
    cv.erode(mask, inner, k);
    cv.dilate(mask, outer, k);
    cv.subtract(outer, mask, outer);
    if (!cv.countNonZero(inner) || !cv.countNonZero(outer)) return 0;
    return (cv.mean(gray, inner)[0] - cv.mean(gray, outer)[0]) / 255;
  } finally {
    [mask, inner, outer, k, poly, pv].forEach((m) => m.delete());
  }
}

// Score candidate quads from a binary map: must have real edges along all sides and be brighter than its surroundings.
function bestQuadIn(cv, bin, gray, edges, best) {
  const W = gray.cols;
  const H = gray.rows;
  const contours = new cv.MatVector();
  const hier = new cv.Mat();
  cv.findContours(bin, contours, hier, cv.RETR_LIST, cv.CHAIN_APPROX_SIMPLE);
  const img = W * H;
  const m = Math.min(W, H) * 0.015;
  for (let i = 0; i < contours.size(); i++) {
    const c = contours.get(i);
    const area = cv.contourArea(c);
    if (area > img * 0.12) {
      const hull = new cv.Mat();
      cv.convexHull(c, hull);
      const q = quadFromContour(cv, hull);
      hull.delete();
      if (q) {
        const qa = polyArea(q);
        const onFrame = q.filter((p) => p.x < m || p.y < m || p.x > W - m || p.y > H - m).length;
        if (qa < img * 0.95 && onFrame < 3 && area / qa > 0.8) {
          const sup = edgeSupport(edges, q);
          const con = sup >= 0.6 ? insideContrast(cv, gray, q) : 0;
          if (sup >= 0.6 && con >= 0.08) {
            const score = sup * con * Math.sqrt(qa / img);
            if (!best || score > best.score) best = { q, score };
          }
        }
      }
    }
    c.delete();
  }
  contours.delete();
  hier.delete();
  return best;
}

// Returns ordered corners [tl,tr,br,bl] in the canvas' pixel space, or null.
export function detectQuad(cv, canvas) {
  const src = cv.imread(canvas);
  const gray = new cv.Mat();
  const bin = new cv.Mat();
  const edges = new cv.Mat();
  const open = cv.getStructuringElement(cv.MORPH_RECT, new cv.Size(7, 7));
  const close = cv.getStructuringElement(cv.MORPH_RECT, new cv.Size(3, 3));
  let best = null;
  try {
    cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY);
    cv.GaussianBlur(gray, gray, new cv.Size(5, 5), 0);
    cv.Canny(gray, edges, 50, 150);
    // 1) Bright paper vs darker background.
    cv.threshold(gray, bin, 0, 255, cv.THRESH_BINARY + cv.THRESH_OTSU);
    cv.morphologyEx(bin, bin, cv.MORPH_OPEN, open);
    best = bestQuadIn(cv, bin, gray, edges, best);
    // 2) Edge contours (paper and floor of similar brightness).
    for (const [lo, hi] of [[30, 100], [60, 180]]) {
      cv.Canny(gray, bin, lo, hi);
      cv.morphologyEx(bin, bin, cv.MORPH_CLOSE, close);
      best = bestQuadIn(cv, bin, gray, edges, best);
    }
  } finally {
    [src, gray, bin, edges, open, close].forEach((x) => x.delete());
  }
  return best ? orderCorners(best.q) : null;
}

// Flatten the quad region of `canvas` into a new canvas. mode: "scan" (clean B&W) | "color" (enhanced color).
export function flatten(cv, canvas, quad, mode = "scan") {
  const [tl, tr, br, bl] = quad;
  const w = Math.round(Math.max(dist(tl, tr), dist(bl, br)));
  const h = Math.round(Math.max(dist(tl, bl), dist(tr, br)));
  const src = cv.imread(canvas);
  const out = new cv.Mat();
  const from = cv.matFromArray(4, 1, cv.CV_32FC2, [tl.x, tl.y, tr.x, tr.y, br.x, br.y, bl.x, bl.y]);
  const to = cv.matFromArray(4, 1, cv.CV_32FC2, [0, 0, w, 0, w, h, 0, h]);
  const M = cv.getPerspectiveTransform(from, to);
  const result = document.createElement("canvas");
  try {
    cv.warpPerspective(src, out, M, new cv.Size(w, h), cv.INTER_LINEAR, cv.BORDER_REPLICATE);
    if (mode === "scan") enhance(cv, out);
    else enhanceColor(cv, out);
    cv.imshow(result, out);
  } finally {
    [src, out, from, to, M].forEach((m) => m.delete());
  }
  return result;
}

// Background light map (paper colour without the ink) used to flatten shadows.
function lightMap(cv, gray) {
  const bg = new cv.Mat();
  const kernel = cv.getStructuringElement(cv.MORPH_RECT, new cv.Size(9, 9));
  cv.dilate(gray, bg, kernel);
  cv.medianBlur(bg, bg, 21);
  kernel.delete();
  return bg;
}

function sharpen(cv, mat, amount = 0.6) {
  const blur = new cv.Mat();
  cv.GaussianBlur(mat, blur, new cv.Size(0, 0), 2.2);
  cv.addWeighted(mat, 1 + amount, blur, -amount, 0, mat);
  blur.delete();
}

// In-place: remove shadows / uneven light, boost contrast and sharpen, CamScanner-style B&W.
function enhance(cv, mat) {
  const gray = new cv.Mat();
  let bg = null;
  try {
    cv.cvtColor(mat, gray, cv.COLOR_RGBA2GRAY);
    bg = lightMap(cv, gray);
    cv.divide(gray, bg, gray, 255);
    cv.normalize(gray, gray, 0, 255, cv.NORM_MINMAX);
    sharpen(cv, gray, 0.7);
    gray.convertTo(gray, -1, 1.3, -45);
    cv.cvtColor(gray, mat, cv.COLOR_GRAY2RGBA);
  } finally {
    gray.delete();
    bg?.delete();
  }
}

// In-place: same shadow flattening per colour channel, keeps stamps/signatures in colour.
function enhanceColor(cv, mat) {
  const gray = new cv.Mat();
  const chans = new cv.MatVector();
  let bg = null;
  try {
    cv.cvtColor(mat, gray, cv.COLOR_RGBA2GRAY);
    bg = lightMap(cv, gray);
    cv.split(mat, chans);
    for (let i = 0; i < 3; i++) {
      const c = chans.get(i);
      cv.divide(c, bg, c, 255);
      c.delete();
    }
    cv.merge(chans, mat);
    sharpen(cv, mat, 0.5);
    mat.convertTo(mat, -1, 1.12, -12);
  } finally {
    gray.delete();
    chans.delete();
    bg?.delete();
  }
}

// Draw any image/video source into a canvas capped at maxSide px on its long side.
export function toCanvas(source, srcW, srcH, maxSide) {
  const scale = Math.min(1, maxSide / Math.max(srcW, srcH));
  const c = document.createElement("canvas");
  c.width = Math.round(srcW * scale);
  c.height = Math.round(srcH * scale);
  c.getContext("2d").drawImage(source, 0, 0, c.width, c.height);
  return c;
}

export const fullQuad = (c) => [{ x: 0, y: 0 }, { x: c.width, y: 0 }, { x: c.width, y: c.height }, { x: 0, y: c.height }];

// Corners placed 15% in from each edge when no page was found: the driver must drag them onto the paper.
export const insetQuad = (c, f = 0.15) => [
  { x: c.width * f, y: c.height * f }, { x: c.width * (1 - f), y: c.height * f },
  { x: c.width * (1 - f), y: c.height * (1 - f) }, { x: c.width * f, y: c.height * (1 - f) },
];

// Find the page outline on a small copy of a still photo (no flattening yet).
export function locate(cv, full) {
  const small = toCanvas(full, full.width, full.height, 800);
  const q = detectQuad(cv, small);
  const k = full.width / small.width;
  if (!q) return { detected: false, quad: insetQuad(full) };
  // Pull corners ~1% toward the centre so no background fringe survives the crop.
  const cx = q.reduce((s, p) => s + p.x, 0) / 4;
  const cy = q.reduce((s, p) => s + p.y, 0) / 4;
  return { detected: true, quad: q.map((p) => ({ x: (p.x + 0.5 + (cx - p.x) * 0.012) * k - 0.5, y: (p.y + 0.5 + (cy - p.y) * 0.012) * k - 0.5 })) };
}

// Fast perspective warp of a small copy, no cleanup — for the live crop preview.
export function warpPreview(cv, small, quad) {
  const [tl, tr, br, bl] = quad;
  const w = Math.max(20, Math.round(Math.max(dist(tl, tr), dist(bl, br))));
  const h = Math.max(20, Math.round(Math.max(dist(tl, bl), dist(tr, br))));
  const src = cv.imread(small);
  const out = new cv.Mat();
  const from = cv.matFromArray(4, 1, cv.CV_32FC2, [tl.x, tl.y, tr.x, tr.y, br.x, br.y, bl.x, bl.y]);
  const to = cv.matFromArray(4, 1, cv.CV_32FC2, [0, 0, w, 0, w, h, 0, h]);
  const M = cv.getPerspectiveTransform(from, to);
  const result = document.createElement("canvas");
  try {
    cv.warpPerspective(src, out, M, new cv.Size(w, h), cv.INTER_LINEAR, cv.BORDER_CONSTANT);
    cv.imshow(result, out);
  } finally {
    [src, out, from, to, M].forEach((m) => m.delete());
  }
  return result;
}

// Slow part: flatten + clean both looks.
export function renderPage(cv, raw, quad) {
  return {
    scan: flatten(cv, raw, quad, "scan").toDataURL("image/jpeg", 0.85),
    color: flatten(cv, raw, quad, "color").toDataURL("image/jpeg", 0.85),
  };
}
